import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface MatchRecord {
  id: string;
  match_number: string | null;
  requester_id: string;
  receiver_id: string;
  requester_role: string;
  receiver_role: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  care_end_date: string | null;
  location: string | null;
  hourly_rate: number | null;
  daily_hours: number | null;
  total_amount: number | null;
  notes: string | null;
  contact_revealed: boolean;
  accepted_at: string | null;
  completed_at: string | null;
  created_at: string;
  requester_name: string;
  requester_phone: string;
  receiver_name: string;
  receiver_phone: string;
}

export type MatchStatusFilter = '전체' | 'accepted' | 'completed' | 'rejected' | 'cancelled';

export function useMatchRequests() {
  const [data, setData] = useState<MatchRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('match_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;
      if (!rows || rows.length === 0) {
        setData([]);
        setLoading(false);
        return;
      }

      const allUserIds = [...new Set(rows.flatMap((r: { requester_id: string; receiver_id: string }) => [r.requester_id, r.receiver_id]))];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, phone')
        .in('id', allUserIds);

      const profileMap = new Map((profiles || []).map((p: { id: string; name: string; phone: string }) => [p.id, p]));

      const enriched: MatchRecord[] = rows.map((r: { requester_id: string; receiver_id: string; match_number: string | null; requester_role: string; receiver_role: string; status: string; start_date: string | null; end_date: string | null; care_end_date: string | null; location: string | null; hourly_rate: number | null; daily_hours: number | null; total_amount: number | null; notes: string | null; contact_revealed: boolean; accepted_at: string | null; completed_at: string | null; created_at: string; id: string }) => {
        const reqProfile = profileMap.get(r.requester_id);
        const recProfile = profileMap.get(r.receiver_id);
        return {
          ...r,
          requester_name: reqProfile?.name || '알 수 없음',
          requester_phone: reqProfile?.phone || '-',
          receiver_name: recProfile?.name || '알 수 없음',
          receiver_phone: recProfile?.phone || '-',
        };
      });

      setData(enriched);
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터 로딩 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}